"use client";

import { useLayoutEffect, useRef, useState } from "react";
import DropDown from "../utils/dropdown-item/DropDown";
import EmptyBox from "../emptystate/EmptyBox";
import LoadingBox from "../loading/LoadingBox";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { cn } from "../../lib/utils";

// Spazio tra toggle e pannello (mt-4 di `.main_comp_dropdow_content`)
const DROPDOWN_GAP = 16;

/**
 * Area utile del primo antenato scrollabile (es. `.wrap_modal_container`),
 * al netto del suo padding. Senza antenati scrollabili si usa il viewport.
 * @param {HTMLElement} node
 */
function getBoundsRect(node) {
	let parent = node?.parentElement;

	while (parent && parent !== document.body) {
		const style = window.getComputedStyle(parent);

		if (/(auto|scroll|overlay)/.test(style.overflowY)) {
			const rect = parent.getBoundingClientRect();

			return {
				top: rect.top + parseFloat(style.paddingTop || 0),
				bottom: rect.bottom - parseFloat(style.paddingBottom || 0),
			};
		}

		parent = parent.parentElement;
	}

	return { top: 0, bottom: window.innerHeight };
}

const SingleSelect = ({
	listItems,
	selectedItem,
	onSelect = () => {},
	classNameOverlay = "",
	placeholder,
	isLoading,
	error,
	disabled,
	searchable,
	searchProps,
}) => {
	// Ricerca interna: usata solo quando il filtro non è gestito dall'esterno
	const [search, setSearch] = useState("");

	const [isOpen, setIsOpen] = useState(false);

	// Apertura verso l'alto: evita di sforare il contenitore scrollabile
	const [dropUp, setDropUp] = useState(false);

	const toggleEl = useRef(null);
	const panelEl = useRef(null);

	const hasSearch = !!searchProps || !!searchable;
	const searchValue = searchProps ? searchProps.value : search;

	/**
	 * `listItems` accetta due formati:
	 * - array piatto di opzioni
	 * - oggetto di gruppi `{ [key]: { id, title, listItems } }`
	 */
	const groups = Array.isArray(listItems)
		? [{ id: "all", title: null, listItems }]
		: Object.entries(listItems ?? {}).map(([key, group]) => ({
				id: group?.id ?? key,
				title: group?.title ?? null,
				listItems: group?.listItems ?? [],
			}));

	// Con `searchProps` la lista arriva già filtrata dal parent
	const filterItems = (list) =>
		searchProps || !searchValue
			? list
			: list.filter((data) =>
					`${data.label ?? ""}`.toLowerCase().includes(searchValue.toLowerCase())
				);

	// Gruppi senza risultati: nascosti (titolo compreso)
	const items = groups
		.map((group) => ({ ...group, listItems: filterItems(group.listItems) }))
		.filter((group) => group.listItems.length > 0);

	const itemsCount = items.reduce((acc, group) => acc + group.listItems.length, 0);

	/**
	 * Placement: apre verso l'alto se sotto non c'è spazio a sufficienza
	 * (evita che il pannello generi scroll nel contenitore)
	 */
	useLayoutEffect(() => {
		if (!isOpen) return;

		function updatePlacement() {
			if (!toggleEl.current || !panelEl.current) return;

			const toggleRect = toggleEl.current.getBoundingClientRect();
			const bounds = getBoundsRect(toggleEl.current);
			const panelHeight = panelEl.current.offsetHeight + DROPDOWN_GAP;

			const spaceBelow = bounds.bottom - toggleRect.bottom;
			const spaceAbove = toggleRect.top - bounds.top;

			setDropUp(panelHeight > spaceBelow && spaceAbove > spaceBelow);
		}

		updatePlacement();

		window.addEventListener("resize", updatePlacement);
		window.addEventListener("scroll", updatePlacement, true);

		return () => {
			window.removeEventListener("resize", updatePlacement);
			window.removeEventListener("scroll", updatePlacement, true);
		};
	}, [isOpen, itemsCount, isLoading]);

	/**
	 * Toggle style: replica gli stati di `select.landing`
	 * (default / hover / focus-open / err / dis)
	 * @param {boolean} isOpen
	 */
	function toggleShadowClass(isOpen) {
		if (disabled) return "shadow-none";

		if (error) {
			return isOpen
				? "shadow-[inset_0_0_0_1px_theme(colors.red.100),0_0_0_3px_theme(colors.red.10)] z-[3]"
				: "shadow-[inset_0_0_0_1px_theme(colors.red.100)] hover:z-[3]";
		}

		return isOpen
			? "shadow-[inset_0_0_0_1px_theme(colors.primary.100),0_0_0_3px_theme(colors.interactive.70)] z-[3]"
			: "shadow-[inset_0_0_0_1px_theme(colors.dark.30)] hover:shadow-[inset_0_0_0_1px_theme(colors.dark.50)] hover:z-[3]";
	}

	/**
	 * Listener Item
	 * @param {option selected} data
	 * @param {function} closeDropDown
	 */
	function onSelectItem(data, closeDropDown) {
		onSelect(selectedItem?.id === data.id ? null : data);
		closeDropDown();
	}

	const renderItem = (data, key, closeDropDown) => {
		const isSelected = selectedItem?.id === data.id;

		return (
			<button
				key={key}
				onClick={() => onSelectItem(data, closeDropDown)}
				className="flex w-full items-center justify-between gap-3 border-0 bg-transparent text-14 text-dark-100 cursor-pointer select-none"
				type="button"
			>
				<div className="flex items-center gap-2 truncate">
					{data.icon && <span className={data.icon}></span>}
					<div className="truncate">{data.label}</div>
				</div>

				{isSelected && (
					<span className="icon_16 default check icon_mask !bg-interactive-100 flex-none" />
				)}
			</button>
		);
	};

	const renderToggle = (isOpen) => (
		<div ref={toggleEl} className="relative w-full z-[2] align-top">
			<div
				className={cn(
					"relative z-0 flex items-center gap-2 w-full min-w-[60px] min-h-[40px]",
					"text-14 font-bold text-dark-70 bg-white rounded-[4px] p-[9px_38px_9px_12px]",
					"cursor-pointer select-none",
					toggleShadowClass(isOpen),
					disabled && "bg-dark-70/10 text-dark-40 select-none cursor-default"
				)}
			>
				{selectedItem?.icon && <span className={cn(selectedItem.icon, "flex-none")} />}
				<div className={cn("truncate", !selectedItem && !disabled && "text-dark-50")}>
					{selectedItem?.label ?? placeholder ?? "Seleziona elementi"}
				</div>
			</div>

			{/* Arrow */}
			<div className="absolute w-5 h-5 right-[10px] top-1/2 -translate-y-1/2 z-[4] pointer-events-none flex items-center justify-center">
				<span
					data-icon-arrow="1"
					className={cn(
						"icon_16 default chevron icon_mask !bg-interactive-100 flex-none",
						disabled && "grayscale"
					)}
				/>
			</div>
		</div>
	);

	// Disabled: nessuna interazione, si mostra solo il toggle
	if (disabled) return <div>{renderToggle(false)}</div>;

	return (
		<div>
			<DropDown
				onToggle={(open) => {
					setIsOpen(open);
					if (!open) setSearch("");
				}}
			>
				{/* Dropdown Toggle */}
				<DropDown.Toggle>{({ isOpen }) => renderToggle(isOpen)}</DropDown.Toggle>

				{/* DropDown Content */}
				<DropDown.Content className={cn("w-full", dropUp && "bottom-full !mt-0 !mb-4")}>
					{({ closeDropDown }) => (
						<div
							ref={panelEl}
							className="bg-white z-30 overflow-auto shadow-[0_8px_20px] shadow-dark-100/10 ring-1 ring-dark-10 rounded-[10px]"
						>
							{hasSearch && (
								<div className="px-5 pt-5">
									<div className="pb-5 shadow-[inset_0_-1px] shadow-dark-10">
										<div className="wrap_input_landing icon">
											<span className="icon_16 default search" />
											<input
												type="text"
												className="landing"
												placeholder="Cerca..."
												value={searchValue}
												onChange={(e) =>
													searchProps
														? searchProps.handler(e.target.value)
														: setSearch(e.target.value)
												}
											/>
										</div>
									</div>
								</div>
							)}
							{isLoading ? (
								<LoadingBox blocked="true" />
							) : itemsCount > 0 ? (
								<OverlayScrollbarsComponent
									defer
									options={{ scrollbars: { autoHide: "never" } }}
									className={cn("overflow-auto p-5 max-h-[250px]", classNameOverlay)}
								>
									<div className="grid gap-4">
										{items.map((group, g) => (
											<div key={group.id ?? g} className="grid gap-4">
												{/* Titolo gruppo: stile di `.main_table_head .main_head_item` */}
												{group.title && (
													<div className="text-12 text-left font-bold whitespace-nowrap uppercase text-dark-50">
														{group.title}
													</div>
												)}

												{group.listItems.map((data, i) =>
													renderItem(data, `${group.id ?? g}-${i}`, closeDropDown)
												)}
											</div>
										))}
									</div>
								</OverlayScrollbarsComponent>
							) : (
								<div className="flex flex-col justify-center items-center text-center">
									<EmptyBox
										small={true}
										icon="icon_75 default archive_plan"
										title="Nessun risultato trovato"
										description="Non ci sono risultati da selezionare"
									/>
								</div>
							)}
						</div>
					)}
				</DropDown.Content>
			</DropDown>
		</div>
	);
};

export default SingleSelect;
